import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Sparkles, Loader2, Store, Sparkle, Users, Crown, Check, PartyPopper } from "lucide-react";
import { toast } from "sonner";

export default function SharedExperience() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [prefs, setPrefs] = useState("");
  const [joined, setJoined] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/experiences/shared/${token}`); setData(data); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Invito non valido"); }
  };
  useEffect(() => { load(); }, [token]);

  const myGuest = (data?.guests || []).find((g) => g.name === user?.name);
  const myStatus = myGuest?.status;

  const act = async (action) => {
    if (!user) { navigate("/login", { state: { from: { pathname: `/esperienze/condivisa/${token}` } } }); return; }
    setBusy(true);
    try {
      const { data: r } = await api.post(`/experiences/shared/${token}/participate`, { action, preferences: prefs });
      const msg = { accept: "Ti sei unito all'esperienza! 🎉", decline: "Invito rifiutato", cancel: "Partecipazione annullata", enter: "Sei entrato nell'esperienza! 🎊" };
      toast.success(r.role === "organizer" ? "Sei l'organizzatore" : msg[action]); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (err) return <div className="min-h-screen bg-[#0A0A0C] text-white flex items-center justify-center px-4"><div className="text-center"><PartyPopper className="h-10 w-10 mx-auto text-zinc-600 mb-3" /><p className="text-zinc-400" data-testid="shared-error">{err}</p><Button onClick={() => navigate("/esperienze")} className="mt-4 bg-[#00E676] text-black rounded-full">Crea la tua esperienza</Button></div></div>;
  if (!data) return <div className="min-h-screen bg-[#0A0A0C] text-white flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white grain" data-testid="shared-experience-page">
      <div className="max-w-lg mx-auto px-4 py-6">
        <div className="rounded-2xl overflow-hidden border border-white/10 relative">
          <div className="h-44 bg-cover bg-center" style={{ backgroundImage: `url(${data.hero_image})` }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div className="text-xs text-[#7DF9FF] flex items-center gap-1 mb-1"><Sparkles className="h-3.5 w-3.5" /> Sei stato invitato a un'esperienza</div>
            <h1 className="font-display font-black text-2xl">{data.title}</h1>
            <div className="text-sm text-zinc-300 flex items-center gap-1 mt-1"><Crown className="h-4 w-4 text-[#FFD700]" /> Organizzata da {data.organizer_name}</div>
          </div>
        </div>

        <div className="mt-4 bg-[#141417] border border-white/10 rounded-2xl p-4">
          <div className="font-semibold mb-2">Le tappe</div>
          <div className="space-y-2">
            {data.steps.map((s, i) => (
              <div key={i} className="flex items-center gap-2 rounded-xl bg-[#0A0A0C] border border-white/10 px-3 py-2" data-testid={`shared-step-${i}`}>
                {s.kind === "service" ? <Sparkle className="h-4 w-4 text-[#7DF9FF] shrink-0" /> : <Store className="h-4 w-4 text-[#00E676] shrink-0" />}
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{s.business_name}</div>
                  {s.title && <div className="text-xs text-zinc-500">{s.title}</div>}
                  {(s.items || []).length > 0 && <div className="text-xs text-zinc-500">{s.items.map((it) => `${it.qty}× ${it.name}`).join(" · ")}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 bg-[#141417] border border-white/10 rounded-2xl p-4">
          <div className="font-semibold mb-2 flex items-center gap-2"><Users className="h-4 w-4 text-[#7DF9FF]" /> Partecipanti ({data.guest_count + 1})</div>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs rounded-full px-3 py-1 bg-[#FFD700]/10 text-[#FFD700] border border-[#FFD700]/30 flex items-center gap-1"><Crown className="h-3 w-3" /> {data.organizer_name}</span>
            {data.guests.map((g, i) => (
              <span key={i} data-testid={`shared-guest-${i}`} className="text-xs rounded-full px-3 py-1 bg-white/5 text-zinc-300 border border-white/15">{g.name}</span>
            ))}
          </div>
        </div>

        {(!myStatus || myStatus === "declined" || myStatus === "cancelled") && (
          <div className="mt-4 bg-[#141417] border border-white/10 rounded-2xl p-4">
            <div className="font-semibold mb-2">Partecipa</div>
            <Textarea data-testid="shared-prefs" value={prefs} onChange={(e) => setPrefs(e.target.value)} rows={2} placeholder="Preferenze o allergie (facoltativo)…" className="bg-[#0A0A0C] border-white/10 resize-none" />
            <div className="flex gap-2 mt-3">
              <Button data-testid="shared-accept" onClick={() => act("accept")} disabled={busy} className="flex-1 bg-gradient-to-r from-[#7DF9FF] to-[#00E676] text-black font-semibold rounded-full">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> Accetta l'invito</>}
              </Button>
              <Button data-testid="shared-decline" onClick={() => act("decline")} disabled={busy} variant="outline" className="rounded-full border-white/15 text-zinc-300">Rifiuta</Button>
            </div>
          </div>
        )}
        {(myStatus === "accepted" || myStatus === "entered") && (
          <div className="mt-4 rounded-2xl bg-[#00E676]/10 border border-[#00E676]/30 p-4" data-testid="shared-joined">
            <div className="text-center text-[#00E676] flex items-center justify-center gap-2 mb-3"><Check className="h-5 w-5" /> {myStatus === "entered" ? "Sei entrato nell'esperienza! 🎊" : "Sei nel gruppo dell'esperienza!"}</div>
            <div className="flex gap-2">
              {myStatus !== "entered" && <Button data-testid="shared-enter" onClick={() => act("enter")} disabled={busy} className="flex-1 bg-[#00E676] text-black font-semibold rounded-full"><PartyPopper className="h-4 w-4 mr-1" /> Entra nell'esperienza</Button>}
              <Button data-testid="shared-cancel" onClick={() => act("cancel")} disabled={busy} variant="outline" className="rounded-full border-white/15 text-zinc-300">Annulla partecipazione</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
